import { useDispatch } from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
import { Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { RECAPTCHA_SITE_KEY } from "../../config/app.config";
import { setRecaptchaStatus } from "../../reducers/RecaptchaStatusReducer";
import { useRecaptchaGate } from "../../Hooks/useRecaptchaGate";

export interface IResilientRecaptchaProps {
    /** Passthrough to the underlying widget's `onChange` (receives the token, or null on reset). */
    onChange: (token: string | null) => void;
    /** Passthrough to the widget's `onExpired`. */
    onExpired?: () => void;
    /** Passthrough to the widget's `onErrored`. Called in addition to reporting the outage centrally. */
    onErrored?: () => void;
    size?: "compact" | "normal" | "invisible";
    /** Remount key the forms use to re-render the widget on breakpoint change (mirrors the old `key`). */
    widgetKey?: string;
}

/**
 * NG-7512: reCaptcha widget with a fail-open fallback.
 *
 * When reCaptcha is bypassed — the `VITE_FEATURE_RECAPTCHA` kill switch is off, or startup
 * detection found the service unavailable (see {@link loadRecaptcha}) — this renders a short
 * notice instead of the widget. The consuming form's submit gate consults the same
 * {@link useRecaptchaGate} hook, so it stays enabled and users are not hard-blocked.
 *
 * On a widget-level load error it also reports the outage to the store as belt-and-suspenders,
 * covering the case where the widget's own script load fails after startup detection passed.
 */
const ResilientRecaptcha = ({ onChange, onExpired, onErrored, size = "compact", widgetKey }: IResilientRecaptchaProps) => {
    const dispatch = useDispatch();
    const { bypassed } = useRecaptchaGate();

    if (bypassed) {
        return (
            <Typography variant="body2" color="text.secondary" data-testid="recaptcha-bypass-notice">
                <FormattedMessage
                    id="recaptcha.notice.message.unavailable"
                    defaultMessage="Verification is temporarily unavailable. You can continue without it."
                />
            </Typography>
        );
    }

    return (
        <ReCAPTCHA
            key={widgetKey}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={onChange}
            onExpired={onExpired}
            onErrored={() => {
                onErrored?.();
                dispatch(setRecaptchaStatus("unavailable"));
            }}
            size={size}
        />
    );
};

export default ResilientRecaptcha;
